import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import SignatureCanvas from "react-signature-canvas";
import {
  ArrowLeft,
  ArrowRight,
  Home,
  Loader2,
  CheckCircle2,
  Eraser,
  ShieldCheck,
} from "lucide-react";
import Nav from "../components/landing/Nav";
import Footer from "../components/landing/Footer";
import ContactDialog from "../components/landing/ContactDialog";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * /rentals/signup — the "Authority to Act as Tax Agent" form for rental
 * clients. Client fills it in, signs on-canvas, submits. Backend generates a
 * PDF of the signed form, emails it to the practice inbox as an attachment,
 * and CCs the client for their records.
 */
export default function RentalsSignupPage() {
  const [contactOpen, setContactOpen] = useState(false);
  const sigRef = useRef(null);
  const [form, setForm] = useState({
    full_name: "",
    ird_number: "",
    address: "",
    contact_number: "",
    email: "",
    authorise_filing: "yes",
    signed_at_location: "Auckland, New Zealand",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const clearSig = () => sigRef.current?.clear();

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    if (!sigRef.current || sigRef.current.isEmpty()) {
      toast.error("Please sign in the box below before submitting.");
      return;
    }
    const signature = sigRef.current.getTrimmedCanvas().toDataURL("image/png");

    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/rentals/signup`, {
        ...form,
        authorise_filing: form.authorise_filing === "yes",
        signature,
      });
      if (res.data?.ok) {
        setDone(true);
        toast.success("Signed authority received — check your inbox for a copy.");
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <main className="min-h-screen bg-[#FAFAFA] text-[#0F172A]" data-testid="rentals-signup-done">
        <Nav
          onCtaClick={() => (window.location.href = "/#pricing")}
          onContactClick={() => setContactOpen(true)}
        />
        <section className="max-w-3xl mx-auto px-6 md:px-10 pt-20 md:pt-28 pb-24 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-[#0A192F] text-[#C9A349]">
            <CheckCircle2 className="w-8 h-8" />
          </div>
          <h1 className="mt-8 font-heading font-black text-4xl md:text-5xl tracking-tight leading-[1.02]">
            Signed and filed.
          </h1>
          <p className="mt-6 text-slate-600 text-lg max-w-xl mx-auto leading-relaxed">
            Your signed <b>Authority to Act</b> has landed in our inbox and a PDF copy is
            on its way to <b>{form.email}</b>. We&apos;ll be in touch shortly with the next
            steps for your rental account.
          </p>
          <div className="mt-10 flex flex-wrap justify-center gap-3">
            <Link
              to="/rentals"
              className="inline-flex items-center gap-2 bg-[#0A192F] text-white px-6 py-4 font-heading font-bold uppercase tracking-[0.14em] text-sm hover:bg-[#C9A349] hover:text-[#0A192F] transition-colors"
              data-testid="rentals-signup-done-back"
            >
              <ArrowLeft className="w-4 h-4" /> Back to rentals
            </Link>
            <a
              href="https://clientops.nz/tools/wealth-plan"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 border border-[#C9A349]/60 bg-[#C9A349]/10 text-[#0A192F] px-6 py-4 font-heading font-bold uppercase tracking-[0.14em] text-sm hover:bg-[#C9A349] transition-colors"
            >
              Try Wealth Plan tool <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </section>
        <Footer />
        <ContactDialog open={contactOpen} onOpenChange={setContactOpen} />
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-[#FAFAFA] text-[#0F172A]" data-testid="rentals-signup-page">
      <Nav
        onCtaClick={() => (window.location.href = "/#pricing")}
        onContactClick={() => setContactOpen(true)}
      />

      {/* Header band */}
      <section className="border-b border-slate-200 bg-white">
        <div className="max-w-4xl mx-auto px-6 md:px-10 pt-10 md:pt-14 pb-8">
          <Link
            to="/rentals"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] font-bold text-slate-500 hover:text-[#C9A349] transition-colors"
            data-testid="rentals-signup-back"
          >
            <ArrowLeft className="w-3 h-3" /> Rentals
          </Link>
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="mt-6 inline-flex items-center gap-2 bg-[#C9A349] text-[#0A192F] px-3 py-1.5 text-[11px] uppercase tracking-[0.22em] font-bold"
          >
            <Home className="w-3 h-3" /> For rental accounts use
          </motion.div>
          <h1 className="mt-5 font-heading font-black text-3xl md:text-5xl tracking-tight leading-[1.02]">
            Authority to Act as Tax Agent <br />
            <span className="text-[#C9A349]">— Engagement / Contract</span>
          </h1>
          <p className="mt-4 text-slate-600 italic text-sm">
            (All information treated with strict confidentiality)
          </p>
        </div>
      </section>

      {/* Form */}
      <form onSubmit={submit} className="max-w-4xl mx-auto px-6 md:px-10 py-12 md:py-16 space-y-10">
        {/* Fee schedule — visible at the top of the engagement form */}
        <div
          className="border-2 border-[#0A192F] bg-white px-6 md:px-8 py-6"
          data-testid="rentals-signup-fee"
        >
          <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-[#C9A349]">
            Annual service fee
          </div>
          <div className="mt-3 grid md:grid-cols-2 gap-5 items-baseline">
            <div>
              <div className="flex items-baseline gap-2">
                <span className="font-heading font-black text-4xl md:text-5xl text-[#0A192F] tracking-tight tabular-nums">
                  $499
                </span>
                <span className="text-base font-heading font-bold text-slate-500">+ GST</span>
              </div>
              <div className="mt-1 text-sm text-slate-600">
                Per rental property, per year — includes <b>one IR3</b>.
              </div>
            </div>
            <div>
              <div className="flex items-baseline gap-2">
                <span className="font-heading font-black text-4xl md:text-5xl text-[#0A192F] tracking-tight tabular-nums">
                  +$100
                </span>
                <span className="text-base font-heading font-bold text-slate-500">+ GST</span>
              </div>
              <div className="mt-1 text-sm text-slate-600">
                Per additional IR3 (most rentals have two owners).
              </div>
            </div>
          </div>
          <div className="mt-4 text-xs text-slate-500 italic border-t border-slate-200 pt-3">
            Worked example: one rental owned 50/50 by a couple = $499 + $100 = <b>$599 + GST per year</b>.
          </div>
        </div>

        {/* Client details */}
        <fieldset className="border-l-4 border-[#C9A349] bg-white px-6 md:px-8 py-7 space-y-5">
          <legend className="text-[11px] uppercase tracking-[0.22em] font-bold text-slate-500 mb-1">
            Client details — Individual
          </legend>

          <FieldRow>
            <Field label="Full name" required>
              <input
                required
                type="text"
                value={form.full_name}
                onChange={(e) => set("full_name", e.target.value)}
                className="input"
                data-testid="rentals-full-name"
                autoComplete="name"
              />
            </Field>
            <Field label="IRD number" required>
              <input
                required
                type="text"
                value={form.ird_number}
                onChange={(e) => set("ird_number", e.target.value)}
                placeholder="000-000-000"
                className="input"
                data-testid="rentals-ird-number"
              />
            </Field>
          </FieldRow>

          <Field label="Contact address" required>
            <textarea
              required
              rows={2}
              value={form.address}
              onChange={(e) => set("address", e.target.value)}
              className="input resize-none"
              data-testid="rentals-address"
              autoComplete="street-address"
            />
          </Field>

          <FieldRow>
            <Field label="Contact number" required>
              <input
                required
                type="tel"
                value={form.contact_number}
                onChange={(e) => set("contact_number", e.target.value)}
                className="input"
                data-testid="rentals-contact-number"
                autoComplete="tel"
              />
            </Field>
            <Field label="Email" required>
              <input
                required
                type="email"
                value={form.email}
                onChange={(e) => set("email", e.target.value)}
                className="input"
                data-testid="rentals-email"
                autoComplete="email"
              />
            </Field>
          </FieldRow>
        </fieldset>

        {/* Authorisation */}
        <fieldset className="border-l-4 border-[#C9A349] bg-white px-6 md:px-8 py-7">
          <legend className="text-[11px] uppercase tracking-[0.22em] font-bold text-slate-500 mb-3">
            Authorisation
          </legend>
          <div className="space-y-4 text-slate-700 text-sm md:text-[15px] leading-relaxed">
            <p>
              I authorise <b>NZ Taxation Limited</b> to act as my tax agent for the purpose of
              preparing and filing my rental and personal tax returns (including IR3R and IR3)
              with Inland Revenue.
            </p>
            <p>
              I grant permission for NZ Taxation Limited to communicate with Inland Revenue on
              my behalf, and to access and provide information related to my revenue obligations
              — including my rental income, expenses, interest deductibility, chattels
              depreciation, and any brightline / ring-fencing calculations.
            </p>
            <p>
              I understand that information may be exchanged between NZ Taxation Limited and
              Inland Revenue electronically, and that records I provide will be kept
              confidentially on my client file.
            </p>
            <p>
              I accept the annual service fee stated at the top of this engagement:{" "}
              <b>NZ$499 + GST</b> per rental property (which includes one IR3 return), plus{" "}
              <b>NZ$100 + GST</b> for each additional IR3 required for co-owners of the property.
              Fees are invoiced annually on completion of the return.
            </p>
          </div>

          <div className="mt-6 pt-5 border-t border-slate-200">
            <div className="font-heading font-bold text-[#0A192F]">
              I authorise NZ Taxation Limited to sign and submit tax returns to Inland Revenue
              on my behalf:
            </div>
            <div className="mt-3 flex gap-6">
              {["yes", "no"].map((val) => (
                <label
                  key={val}
                  className="inline-flex items-center gap-2 cursor-pointer select-none"
                  data-testid={`rentals-authorise-${val}`}
                >
                  <input
                    type="radio"
                    name="authorise"
                    value={val}
                    checked={form.authorise_filing === val}
                    onChange={(e) => set("authorise_filing", e.target.value)}
                    className="w-4 h-4 accent-[#C9A349]"
                  />
                  <span className="font-heading font-bold uppercase tracking-[0.14em] text-sm">
                    {val}
                  </span>
                </label>
              ))}
            </div>
          </div>
        </fieldset>

        {/* Signature */}
        <fieldset className="border-l-4 border-[#C9A349] bg-white px-6 md:px-8 py-7">
          <legend className="text-[11px] uppercase tracking-[0.22em] font-bold text-slate-500 mb-3">
            Authorised signatory
          </legend>
          <p className="text-slate-600 text-sm mb-4">
            Sign in the box below using your mouse, finger, or stylus. Your signature will be
            embedded into the PDF authority form we file against your rental account.
          </p>

          <div className="relative border border-slate-300 bg-white">
            <SignatureCanvas
              ref={sigRef}
              penColor="#0A192F"
              canvasProps={{
                className: "w-full h-48 md:h-56",
                "data-testid": "rentals-signature-canvas",
              }}
            />
            <button
              type="button"
              onClick={clearSig}
              className="absolute top-2 right-2 inline-flex items-center gap-1 text-xs text-slate-500 hover:text-[#C9A349] transition-colors bg-white/80 px-2 py-1"
              data-testid="rentals-signature-clear"
            >
              <Eraser className="w-3 h-3" /> Clear
            </button>
          </div>

          <div className="mt-4 grid md:grid-cols-2 gap-4">
            <Field label="Signed at (location)">
              <input
                type="text"
                value={form.signed_at_location}
                onChange={(e) => set("signed_at_location", e.target.value)}
                className="input"
                data-testid="rentals-signed-at"
              />
            </Field>
            <div className="flex items-end text-sm text-slate-500">
              Date and time will be captured automatically on submit.
            </div>
          </div>
        </fieldset>

        {/* Disclaimer */}
        <div className="text-xs text-slate-500 italic leading-relaxed max-w-3xl">
          Please note: any records not provided in a timely manner may result in your return
          being filed after the statutory deadline. Inland Revenue may impose late filing
          penalties, use-of-money interest, and shortfall penalties which are the responsibility
          of the client, not NZ Taxation Limited.
        </div>

        {/* Submit */}
        <div className="flex flex-wrap items-center gap-4">
          <button
            type="submit"
            disabled={submitting}
            className="inline-flex items-center gap-2 bg-[#0A192F] text-white px-8 py-5 font-heading font-bold uppercase tracking-[0.14em] text-sm hover:bg-[#C9A349] hover:text-[#0A192F] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            data-testid="rentals-signup-submit"
          >
            {submitting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" /> Filing your authority…
              </>
            ) : (
              <>
                <ShieldCheck className="w-4 h-4" /> Sign & submit authority
              </>
            )}
          </button>
          <span className="text-sm text-slate-500">
            A PDF copy will be emailed to you and to our practice inbox.
          </span>
        </div>
      </form>

      <Footer />
      <ContactDialog open={contactOpen} onOpenChange={setContactOpen} />

      {/* Local styles for input elements to keep the JSX compact */}
      <style>{`
        .input {
          width: 100%;
          padding: 12px 14px;
          border: 1px solid #cbd5e1;
          background: #fff;
          font-size: 15px;
          color: #0f172a;
          outline: none;
          transition: border-color 120ms ease;
        }
        .input:focus { border-color: #C9A349; box-shadow: 0 0 0 3px rgba(201,163,73,0.15); }
      `}</style>
    </main>
  );
}

function FieldRow({ children }) {
  return <div className="grid md:grid-cols-2 gap-4">{children}</div>;
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="block text-[11px] uppercase tracking-[0.18em] font-bold text-slate-600 mb-1.5">
        {label} {required && <span className="text-[#C9A349]">*</span>}
      </span>
      {children}
    </label>
  );
}
